import { useState } from "react";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import type { ItemDetail } from "../aidwops.types";
import { ApiError } from "../expertApi";
import "./ExpertQueue.scss";

export type RequestKind = "whitelist_request" | "ssl_bypass_request";

const MIN_JUSTIFICATION = 10;

/**
 * Raises a whitelist or SSL-bypass request. It applies nothing: the backend creates a queue item and runs its
 * pre-flight, and a person still decides. A request for a URL that is already in the queue answers 409 with the
 * id of the item that holds it, which this form offers to open.
 */
export function RaiseRequestForm({
    onSubmit,
    onCreated,
    onOpenExisting,
    onCancel,
}: {
    onSubmit: (
        url: string,
        justification: string,
        kind: RequestKind
    ) => Promise<ItemDetail>;
    onCreated: (item: ItemDetail) => void;
    onOpenExisting: (id: string) => void;
    onCancel: () => void;
}) {
    const [url, setUrl] = useState("");
    const [justification, setJustification] = useState("");
    const [kind, setKind] = useState<RequestKind>("whitelist_request");
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [existingId, setExistingId] = useState<string | null>(null);

    const isValid =
        url.trim().length >= 3 &&
        justification.trim().length >= MIN_JUSTIFICATION;

    const submit = async () => {
        if (!isValid || isSending) {
            return;
        }

        setIsSending(true);
        setError(null);
        setExistingId(null);
        try {
            onCreated(await onSubmit(url.trim(), justification.trim(), kind));
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : String(caught));
            setExistingId(
                caught instanceof ApiError ? (caught.existingId ?? null) : null
            );
        } finally {
            setIsSending(false);
        }
    };

    return (
        <form
            className="raise-form"
            onSubmit={(event) => {
                event.preventDefault();
                void submit();
            }}
        >
            <div className="raise-form-row">
                <label htmlFor="raise-kind">What for</label>
                <select
                    id="raise-kind"
                    value={kind}
                    onChange={(event) =>
                        setKind(event.target.value as RequestKind)
                    }
                >
                    <option value="whitelist_request">
                        Whitelist a URL (unblock it)
                    </option>
                    <option value="ssl_bypass_request">
                        Bypass SSL inspection for a URL
                    </option>
                </select>
            </div>
            <div className="raise-form-row">
                <label htmlFor="raise-url">Domain or URL</label>
                <input
                    id="raise-url"
                    type="text"
                    value={url}
                    onChange={(event) => setUrl(event.target.value)}
                    placeholder="example.com"
                    autoComplete="off"
                />
            </div>
            <div className="raise-form-row">
                <label htmlFor="raise-justification">Business reason</label>
                <textarea
                    id="raise-justification"
                    rows={2}
                    maxLength={2000}
                    value={justification}
                    onChange={(event) => setJustification(event.target.value)}
                    placeholder="Why this is needed (at least 10 characters)"
                />
            </div>
            {error && (
                <p className="queue-decision__error" role="alert">
                    {error}
                    {existingId && (
                        <>
                            {" "}
                            <button
                                type="button"
                                className="triage-link"
                                onClick={() => onOpenExisting(existingId)}
                            >
                                Open {existingId}
                            </button>
                        </>
                    )}
                </p>
            )}
            <div className="raise-form-actions">
                <EonUiButton
                    text="Cancel"
                    rank="secondary"
                    size="small"
                    disabled={isSending}
                    onClick={onCancel}
                />
                <EonUiButton
                    text="Raise request"
                    rank="primary"
                    size="small"
                    disabled={!isValid || isSending}
                    preloader={isSending}
                    onClick={() => void submit()}
                />
            </div>
        </form>
    );
}

export default RaiseRequestForm;
